import { useEffect, useState } from "react";
import { Col, Row, Space } from "antd";
import { FormBox, InputBox } from "../../../../../components/AntdAddons";
import { rules } from "./rules";
import { fetchWebUserList } from "../../../../../store/CommonSlice";
import { fetchIndustryList } from "../../../Industry/utils/slice";
import { useAppDispatch } from "../../../../../utils/hooks/hooks";
import { formRules } from "../../../../../config/validations/formRules";
import { formatMobileNumber } from "../../../../../utils/commonFunctions";
import React from "react";

/**
 * Renders a form component for adding or editing a record in the Cms module.
 *
 * @param {object} props - The component props.
 * @param {object} props.form - The form instance.
 * @param {string} props.id - The form ID.
 * @param {function} props.handleSubmit - The function to handle form submission.
 * @param {function} props.onValuesChange - The function to handle form value changes and update the form fields.
 * @param {object} props.editValues - The values to edit in the form fields, if value already exists.
 * @return {JSX.Element} The rendered form component.
 */
const FormComponent: React.FC<any> = ({
    form,
    id,
    handleSubmit,
    onValuesChange,
    editValues,
}) => {

    const [isEdit, setIsEdit] = useState<boolean>(true);
    const [selectedUserType, setSelectedUserType] = useState<boolean>(true);
    const [webUserList, setWebUserList] = useState<any>([]);
    const [industryList, setIndustryList] = useState<any>([]);

    const dispatch = useAppDispatch();
    useEffect(() => {
        form.setFieldValue('selected_type', true);
        setIsEdit(false);
        if (editValues) {
            const industry_id = editValues.industries.length > 0 ? editValues.industries.map((item: any) => item.id) : [];
            form.setFieldsValue({ ...editValues, ...{ industry_id: industry_id } });
            setIsEdit(true);
        }
        else {
            dispatch(fetchWebUserList())
                .then((data: any) => {
                    const userMaped = data.rows.map((item: any) => {
                        const obj = { ...item };
                        obj.name = `${item.name} - ${item.email}`;
                        return obj;
                    })
                    setWebUserList(userMaped);
                })
                .catch((error: any) => error);


        }
        dispatch(fetchIndustryList())
            .then((data: any) => {
                setIndustryList(data.rows);
            })
            .catch((error: any) => error);

    }, [editValues, form]);

    /**
     * Process the form data and handle the form submission.  
     */
    const onFinish = (data: any) => {
        handleSubmit(data);
    };

    return (
        <>
            <FormBox
                form={form}
                id={id}
                onFinish={onFinish}
                onValuesChange={onValuesChange}
            >
                <Row gutter={15}>
                    <Col xs={{ span: 24 }} md={{ span: 24 }} xxl={{ span: 12 }}>
                        <InputBox.Text required name="company_name" label="Customer Name" />
                    </Col>

                    <Col xs={{ span: 24 }} md={{ span: 24 }} xxl={{ span: 12 }}>
                        <InputBox.Select
                            mode="multiple"
                            required
                            label="Industries"
                            name="industry_id"
                            showSearch
                            className="multiSelect"
                            optionFilterProp="children"
                            filterOption={(input, option: any) =>
                                option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                                0
                            }
                            options={{
                                list: industryList,
                                valueKey: "id",
                                textKey: "industry_name",
                            }}
                            disabled={isEdit}
                        />

                    </Col>
                    {!isEdit &&
                        <>
                            <Col xs={{ span: 24 }} md={{ span: 24 }}>
                                <InputBox.Radio
                                    name="selected_type"
                                    required
                                    label="Select Customer"
                                    options={{
                                        list: [{
                                            value: true,
                                            name: 'Create New'
                                        },
                                        {
                                            value: false,
                                            name: 'Existing Web User'
                                        }],
                                        valueKey: "value",
                                        textKey: "name",
                                    }}
                                    value={selectedUserType}
                                    onChange={(e) => setSelectedUserType(e.target.value)}
                                />
                            </Col>
                        </>
                    }

                    {!isEdit && !selectedUserType &&
                        <Col xs={{ span: 24 }} md={{ span: 24 }} xxl={{ span: 12 }}>
                            <InputBox.Select
                                required
                                label="User"
                                name="user_id"
                                showSearch
                                className="singleSelect"
                                optionFilterProp="children"
                                filterOption={(input, option: any) =>
                                    option?.children?.toLowerCase().indexOf(input?.toLowerCase()) >= 0
                                }
                                options={{
                                    list: webUserList,
                                    valueKey: "id",
                                    textKey: "name",
                                }}
                            />
                        </Col>
                    }
                    {!isEdit && selectedUserType &&
                        <>
                            <br />
                            <br />
                            <Col xs={{ span: 24 }}>
                                <b>Customer Details:</b>
                            </Col>
                            <br />
                            <br />

                            <Col xs={{ span: 24 }} md={{ span: 12 }}>
                                <InputBox.Text
                                    required
                                    label="First Name"
                                    name="name"
                                    rules={rules.name()}
                                />
                            </Col>
                            <Col xs={{ span: 24 }} md={{ span: 12 }}>
                                <InputBox.Text
                                    required
                                    label="Last Name"
                                    name="last_name"
                                    rules={rules.last_name()}
                                />
                            </Col>
                            <Col xs={{ span: 24 }} md={{ span: 12 }}>
                                <InputBox.Text
                                    label="Mobile Number"
                                    name="mobile_no"
                                    maxLength={10}
                                    placeholder="000-000-0000"
                                    rules={formRules.mobileNumber()}
                                    onChange={(e: any) => {
                                        if (e.target.value.length > 0) {
                                            form.setFieldsValue({
                                                mobile_no: formatMobileNumber(e.target.value),
                                            });
                                        }
                                    }}
                                />
                            </Col>
                            <Col xs={{ span: 24 }} md={{ span: 12 }}>
                                <InputBox.Text
                                    required
                                    name="email"
                                    label="Email"
                                    rules={rules.email()}
                                    disabled={editValues ? true : false}
                                />
                            </Col>
                        </>
                    }


                </Row>
            </FormBox>
        </>
    );
};

export default FormComponent;
